import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Unstable_Grid2 as Grid,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/pt-br";
import dayjs from "dayjs";
import React, { FC, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Layout } from "../../components/layout";
import useAuth from "../../hooks/useAuth";
import { recuperarTodosAlertas } from "../../services/alertaUsuario";
import { recuperarTodosPerfis } from "../../services/perfil";
import {
  recuperarUsuarioPorId,
  salvarUsuario,
  atualizarUsuarioPorId,
} from "../../services/usuario";
import {
  Usuario,
  ListagemAlertaUsuario,
  ListagemPerfil,
  AxiosResponse,
  Response,
} from "../../types";

const TelaUsuario: FC = () => {
  const navigate = useNavigate();
  dayjs.locale("pt-br");
  const { user, editUser, signout, setEditUser } = useAuth();
  const [alertasUsuario, setAlertasUsuario] =
    useState<ListagemAlertaUsuario[]>();
  const [perfis, setPerfis] = useState<ListagemPerfil[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [usuario, setUsuario] = useState<Usuario>({
    id: null,
    tipoUsuario: editUser ? editUser.tipo : "usuario",
    alertaId: "1",
    perfilId: "1",
    cadastradoPor: "",
    usuario: "",
    nome: "",
    senha: "",
    email: "",
    celular: "",
    dataVencimento:
      editUser?.tipo === "TESTE"
        ? dayjs().add(2, "day").format("DD/MM/YYYY")
        : "",
    dataNascimento: "",
    conexao: 1,
    valorCobrado: "",
    prePago: "N",
    xml: "S",
    mensagemInterna: "",
    obs: "",
    limiteTeste: "",
    limiteUser: "",
    bloqueado: "N",
  });

  let title = "";

  if (editUser?.idUsuario) {
    title = "Editar";
  } else {
    title = "Cadastrar";
  }

  useEffect(() => {
    if (user?.idUsuario) {
      Promise.all([
        recuperarTodosAlertas()
          .then((response: AxiosResponse<ListagemAlertaUsuario[]>) => {
            if (response) {
              setAlertasUsuario(response.data);
            }
          })
          .catch((error: any) => {
            if (error.response && error.response.status === 401) {
              toast.info("Desconectado por inatividade");
              signout();
              navigate("/");
            } else {
              toast.error("Erro ao recuperar alertas: " + error.message);
            }
          }),
        recuperarTodosPerfis()
          .then((response: AxiosResponse<ListagemPerfil[]>) => {
            if (response) {
              setPerfis(response.data);
            }
          })
          .catch((error: any) => {
            if (error.response && error.response.status === 401) {
              toast.info("Desconectado por inatividade");
              signout();
              navigate("/");
            } else {
              toast.error("Erro ao recuperar perfis: " + error.message);
            }
          }),
      ]);
    }
  }, [user]);

  useEffect(() => {
    if (editUser?.idUsuario) {
      Promise.all([
        recuperarUsuarioPorId(editUser.idUsuario)
          .then((response: AxiosResponse<Usuario>) => {
            if (response) {
              let fetchedUser = response.data;
              if (
                fetchedUser.tipoUsuario === "ADMIN" ||
                fetchedUser.tipoUsuario === "REVENDA"
              ) {
                fetchedUser = { ...fetchedUser, senha: "" };
              }
              setUsuario(fetchedUser);
            }
          })
          .catch((error: any) => {
            if (error.response && error.response.status === 401) {
              toast.info("Desconectado por inatividade");
              signout();
              navigate("/");
            } else {
              toast.error("Erro ao recuperar o usuário:" + error.message);
            }
          }),
      ]);
    }
  }, [editUser]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsLoading(true);

      if (!user || !user.idUsuario) {
        toast.error(
          "Erro: Usuário não está autenticado ou ID do usuário não disponível.",
        );
        setIsLoading(false);
        return;
      }

      if (usuario.tipoUsuario === "TESTE") {
        const dataEsperada = dayjs().startOf("day").add(2, "day");

        if (!dayjs(usuario.dataVencimento, "DD/MM/YYYY").isSame(dataEsperada)) {
          toast.error(
            "A data de vencimento para TESTE deve ser exatamente 2 dias a partir de hoje.",
          );
          setIsLoading(false);
          return;
        }
      }

      if (
        usuario.dataVencimento &&
        dayjs(usuario.dataVencimento, "DD/MM/YYYY")
          .startOf("day")
          .isBefore(dayjs().startOf("day").add(1, "day"))
      ) {
        toast.error("A data de vencimento deve ser maior que hoje.");
        setIsLoading(false);
        return;
      }

      const usuarioParaSalvar: Usuario = {
        ...usuario,
        cadastradoPor: user!.idUsuario,
      };

      if (editUser?.idUsuario) {
        const usuarioparaEditar: Usuario = {
          ...usuario,
        };

        atualizarUsuarioPorId(editUser.idUsuario, usuarioparaEditar)
          .then((response: AxiosResponse<Response>) => {
            if (response) {
              toast.success("Usuário atualizado com sucesso!");
              setEditUser(null);
              navigate(-1);
            }
          })
          .catch((error) => {
            let errorMessage = "";
            if (
              error.response &&
              error.response.data &&
              error.response.data.response
            ) {
              errorMessage += error.response.data.response;
            } else {
              errorMessage += error.message;
            }
            toast.error(errorMessage);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        salvarUsuario(usuarioParaSalvar)
          .then((response: AxiosResponse<Response>) => {
            if (response) {
              toast.success("Usuário salvo com sucesso!");
              setEditUser(null);
              navigate(-1);
            }
          })
          .catch((error) => {
            let errorMessage = "";
            if (
              error.response &&
              error.response.data &&
              error.response.data.response
            ) {
              errorMessage += error.response.data.response;
            } else {
              errorMessage += error.message;
            }
            toast.error(errorMessage);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [usuario],
  );

  const handleChangeDataVencimento = (date: string | null) => {
    if (date !== null) {
      setUsuario({
        ...usuario,
        dataVencimento: date,
      });
    }
  };

  const handleChangeDataNascimento = (date: string | null) => {
    if (date !== null) {
      setUsuario({
        ...usuario,
        dataNascimento: date,
      });
    }
  };

  const handleCancelar = () => {
    setEditUser(null);
    navigate(-1);
  };

  const validarNome = (valor: string) => {
    const regex = /^[a-zA-Z0-9]*$/;
    return regex.test(valor);
  };

  const validarSenha = (valor: string) => {
    const regex = /^\S*$/;
    return regex.test(valor);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Layout>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Card>
            <CardHeader
              subheader="Essas informações podem ser editadas"
              title={title}
            />
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ m: -1.5 }}>
                <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Alertas"
                      name="alertas"
                      required
                      select
                      SelectProps={{ native: true }}
                      value={usuario.alertaId}
                      onChange={(event) =>
                        setUsuario({ ...usuario, alertaId: event.target.value })
                      }
                    >
                      <option key={0} value={0}>
                        {"Selecione"}
                      </option>
                      {alertasUsuario?.map((alerta) => (
                        <option key={alerta.id} value={alerta.id}>
                          {alerta.descricao}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Perfil"
                      name="perfil"
                      required
                      select
                      SelectProps={{ native: true }}
                      value={usuario.perfilId}
                      onChange={(event) =>
                        setUsuario({ ...usuario, perfilId: event.target.value })
                      }
                    >
                      <option key={0} value={0}>
                        {"Selecione"}
                      </option>
                      {perfis?.map((perfil) => (
                        <option key={perfil.id} value={perfil.id}>
                          {perfil.nome}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Usuário"
                      name="usuario"
                      required
                      value={usuario.usuario}
                      onChange={(event) => {
                        const valor = event.target.value;
                        if (validarNome(valor)) {
                          setUsuario({ ...usuario, usuario: valor });
                        } else {
                          toast.error(
                            "Usuário não deve conter espaços ou caracteres especiais.",
                          );
                        }
                      }}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nome"
                      name="nome"
                      required
                      value={usuario.nome}
                      onChange={(event) =>
                        setUsuario({ ...usuario, nome: event.target.value })
                      }
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Senha"
                      name="senha"
                      required={
                        !editUser ||
                        editUser.idUsuario === null ||
                        (editUser.idUsuario !== null &&
                          editUser.tipo !== "ADMIN" &&
                          editUser.tipo !== "REVENDA")
                      }
                      value={usuario.senha}
                      onChange={(event) => {
                        const valor = event.target.value;
                        if (validarSenha(valor)) {
                          setUsuario({ ...usuario, senha: valor });
                        } else {
                          toast.error("Senha não deve conter espaços.");
                        }
                      }}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      value={usuario.email}
                      onChange={(event) =>
                        setUsuario({ ...usuario, email: event.target.value })
                      }
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Celular"
                      name="celular"
                      value={usuario.celular}
                      onChange={(event) =>
                        setUsuario({ ...usuario, celular: event.target.value })
                      }
                    />
                  </Grid>
                  <Grid xs={12} md={3}>
                    <DatePicker
                      label="Vencimento"
                      minDate={dayjs().add(1, "day")}
                      slotProps={{
                        textField: {
                          required: true,
                        },
                      }}
                      value={
                        usuario.dataVencimento
                          ? dayjs(usuario.dataVencimento, "DD/MM/YYYY")
                          : null
                      }
                      onChange={(date) =>
                        handleChangeDataVencimento(
                          date ? dayjs(date).format("DD/MM/YYYY") : null,
                        )
                      }
                    />
                  </Grid>
                  {editUser?.tipo !== "REVENDA" && (
                    <>
                      <Grid xs={12} md={3}>
                        <DatePicker
                          label="Nascimento"
                          value={
                            usuario.dataNascimento
                              ? dayjs(usuario.dataNascimento, "DD/MM/YYYY")
                              : null
                          }
                          onChange={(date) =>
                            handleChangeDataNascimento(
                              date ? dayjs(date).format("DD/MM/YYYY") : null,
                            )
                          }
                        />
                      </Grid>
                    </>
                  )}
                  {editUser?.tipo === "USUARIO" && (
                    <>
                      <Grid xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Qtd. Conexões"
                          name="conexao"
                          type="number"
                          value={usuario.conexao}
                          onChange={(event) => {
                            const selectedValue = event.target.value;
                            const numericValue = Number(selectedValue);
                            setUsuario({
                              ...usuario,
                              conexao: isNaN(numericValue) ? 0 : numericValue,
                            });
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Valor Cobrado"
                      name="valorCobrado"
                      value={usuario.valorCobrado}
                      onChange={(event) =>
                        setUsuario({
                          ...usuario,
                          valorCobrado: event.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Pré Pago"
                      name="prePago"
                      select
                      SelectProps={{ native: true }}
                      value={usuario.prePago}
                      onChange={(event) =>
                        setUsuario({ ...usuario, prePago: event.target.value })
                      }
                    >
                      <option value="S">Sim</option>
                      <option value="N">Não</option>
                    </TextField>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="XML"
                      name="xml"
                      select
                      SelectProps={{ native: true }}
                      value={usuario.xml}
                      onChange={(event) =>
                        setUsuario({ ...usuario, xml: event.target.value })
                      }
                    >
                      <option value="S">Sim</option>
                      <option value="N">Não</option>
                    </TextField>
                  </Grid>
                  {editUser?.tipo === "REVENDA" && (
                    <>
                      <Grid xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Limite Teste"
                          name="limiteTeste"
                          type="number"
                          value={usuario.limiteTeste}
                          onChange={(event) =>
                            setUsuario({
                              ...usuario,
                              limiteTeste: event.target.value,
                            })
                          }
                        />
                      </Grid>
                      <Grid xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Limite User"
                          name="limiteUser"
                          type="number"
                          value={usuario.limiteUser}
                          onChange={(event) =>
                            setUsuario({
                              ...usuario,
                              limiteUser: event.target.value,
                            })
                          }
                        />
                      </Grid>
                    </>
                  )}
                  <Grid xs={12} md={12}>
                    <TextField
                      fullWidth
                      label="Mensagem Interna"
                      name="mensagemInterna"
                      value={usuario.mensagemInterna}
                      onChange={(event) =>
                        setUsuario({
                          ...usuario,
                          mensagemInterna: event.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Obs"
                      name="obs"
                      value={usuario.obs}
                      onChange={(event) =>
                        setUsuario({
                          ...usuario,
                          obs: event.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Bloqueado"
                      name="bloqueado"
                      select
                      SelectProps={{ native: true }}
                      value={usuario.bloqueado}
                      onChange={(event) =>
                        setUsuario({
                          ...usuario,
                          bloqueado: event.target.value,
                        })
                      }
                    >
                      <option value="S">Sim</option>
                      <option value="N">Não</option>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <Button type="submit" variant="contained" disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} /> : "Salvar"}
              </Button>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCancelar}
                disabled={isLoading}
              >
                Cancelar
              </Button>
            </CardActions>
          </Card>
        </form>
      </Layout>
    </LocalizationProvider>
  );
};

export default TelaUsuario;
